import {
  type ButtonConfig,
  DisabledZoomControls,
  type SettingsConfig,
  SettingsType,
  type ToggleConfig,
  ViewContext,
} from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import { isTruthy } from '@ardoq/common-helpers';
import { ViewIds } from '@ardoq/api-types';
import {
  collapseAll,
  expandAll,
} from 'tabview/relationshipDiagrams/leftMenuItems';
import {
  BlockDiagramViewProperties,
  ModernizedBlockDiagramViewProperties,
} from 'tabview/blockDiagram/types';
import {
  isViewpointModeDependencyMapSettingsControlsDependencies,
  toViewpointModeViewActions as toDependencyMapViewpointModeViewActions,
} from 'tabview/dependencyMap/viewpointMode/settingsControls';
import { isTimelineSettingsControlsDependencies } from '@ardoq/timeline2024';
import {
  isViewpointModeBubbleChartSettingsControlsDependencies,
  toViewpointModeViewActions,
} from 'tabview/bubbleChart/viewpointMode/settingsControls';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  componentMatrixCollapseAll,
  componentMatrixExpandAll,
} from 'tabview/componentMatrix/actions';
import type { ViewpointModeComponentMatrixViewStreamState } from 'tabview/componentMatrix/componentMatrix2/types';
import {
  collapseAllAssistantDiagramGroups,
  expandAllAssistantDiagramGroups,
} from 'tabview/assistantDiagram/assistantDiagram$';
import type { AssistantDiagramViewModel } from 'tabview/assistantDiagram/viewModel$';
import { type AssistantBlocksViewProperties } from 'tabview/assistantBlocks/viewModel$';
import {
  assistantBlocksCollapseAll,
  assistantBlocksExpandAll,
} from 'tabview/assistantBlocks/assistantBlocks$';
import {
  collapseAllButton,
  expandAllButton,
} from 'tabview/assistantShared/groupCollapseButtons';
import {
  flexibleBlockDiagramCollapseAll,
  flexibleBlockDiagramExpandAll,
} from 'tabview/flexibleBlockDiagram/actions';
import { isFlexibleBlockDiagramViewProperties } from 'tabview/flexibleBlockDiagram/types';
import {
  collapseAllButton as network3dCollapseAllButton,
  expandAllButton as network3dExpandAllButton,
} from 'tabview/network3d/network3dMenus';
import { isNetwork3DViewProperties } from 'tabview/network3d/types';
import { layoutTypeDropdown } from 'tabview/flexibleBlockDiagram/menus';
import { t, type I18nLocale } from '@ardoq/i18n';
import {
  isRelationshipsSettingsControlsDependencies,
  toViewpointModeRelationshipsSettingsControlsDependencies,
  toViewpointModeRelationshipsViewActions,
} from 'tabview/relationsD3View/hierarchical/viewpointMode/settingsControls';
import { trackOptimizeLayoutClick } from './tracking';

const viewActionsOptions = (
  viewId: ViewIds,
  viewProperties: Record<string, any>,
  {
    zoomIn,
    zoomOut,
    optimizeLayout,
    zoomControls,
    zoomControlsDisabledState,
  }: ViewContext,
  i18nLocale: I18nLocale
): SettingsConfig[] => {
  const defaultViewActionsConfig =
    zoomControls ??
    [
      zoomIn &&
        ({
          id: 'zoomIn',
          type: SettingsType.BUTTON,
          label: t(i18nLocale, 'appLayout:viewPaneHeader.zoomIn', 'Zoom in'),
          iconName: IconName.ZOOM_IN,
          onClick: zoomIn,
          isDisabled: Boolean(
            zoomControlsDisabledState & DisabledZoomControls.ZOOM_IN
          ),
        } satisfies ButtonConfig),
      zoomOut &&
        ({
          id: 'zoomOut',
          type: SettingsType.BUTTON,
          label: t(i18nLocale, 'appLayout:viewPaneHeader.zoomOut', 'Zoom out'),
          iconName: IconName.ZOOM_OUT,
          onClick: zoomOut,
          isDisabled: Boolean(
            zoomControlsDisabledState & DisabledZoomControls.ZOOM_OUT
          ),
        } satisfies ButtonConfig),
      optimizeLayout &&
        ({
          id: 'optimizeLayout',
          type: SettingsType.BUTTON,
          label: t(
            i18nLocale,
            'appLayout:viewPaneHeader.optimizeLayout',
            'Optimize layout'
          ),
          iconName: IconName.OPTIMIZE_LAYOUT,
          onClick: () => {
            trackOptimizeLayoutClick(viewId);
            optimizeLayout();
          },
          isDisabled: Boolean(
            zoomControlsDisabledState & DisabledZoomControls.FIT_WINDOW
          ),
        } satisfies ButtonConfig),
    ].filter(isTruthy);
  // The Assistant Diagram registers no `viewSettings`, so it's handled before the guard
  // below. Collapse/expand-all disabled state comes from the diagram's live fold state.
  if (viewId === ViewIds.ASSISTANT_DIAGRAM) {
    const { foldState } = viewProperties as AssistantDiagramViewModel;
    return [
      {
        id: 'collapseAll',
        label: t(
          i18nLocale,
          'appLayout:viewPaneHeader.collapseAll',
          'Collapse all'
        ),
        iconName: IconName.COLLAPSE_ALL,
        type: SettingsType.BUTTON,
        isDisabled: foldState.groupCount - foldState.collapsedCount === 0,
        onClick: () => dispatchAction(collapseAllAssistantDiagramGroups()),
      } satisfies ButtonConfig,
      {
        id: 'expandAll',
        label: t(
          i18nLocale,
          'appLayout:viewPaneHeader.expandAll',
          'Expand all'
        ),
        iconName: IconName.EXPAND_ALL,
        type: SettingsType.BUTTON,
        isDisabled: foldState.collapsedCount === 0,
        onClick: () => dispatchAction(expandAllAssistantDiagramGroups()),
      } satisfies ButtonConfig,
      ...defaultViewActionsConfig,
    ];
  }
  // Assistant Blocks registers no `viewSettings` either.
  if (viewId === ViewIds.ASSISTANT_BLOCKS) {
    const { hasGroups } = viewProperties as AssistantBlocksViewProperties;
    return [
      collapseAllButton({
        viewId,
        hasGroups,
        action: assistantBlocksCollapseAll,
        i18nLocale,
      }),
      expandAllButton({
        viewId,
        hasGroups,
        action: assistantBlocksExpandAll,
        i18nLocale,
      }),
      ...defaultViewActionsConfig,
    ];
  }
  if (!('viewSettings' in viewProperties)) {
    return defaultViewActionsConfig;
  }
  switch (viewId) {
    case ViewIds.BLOCK_DIAGRAM: {
      const { viewModel, viewSettings, viewInstanceId } =
        viewProperties as BlockDiagramViewProperties;
      const hasNoGroups =
        !viewModel.groups.add.length && !viewModel.groups.update.length;
      return [
        collapseAll(
          viewSettings,
          viewId,
          viewInstanceId,
          hasNoGroups,
          i18nLocale
        ),
        expandAll(
          viewSettings,
          viewId,
          viewInstanceId,
          hasNoGroups,
          i18nLocale
        ),
        ...defaultViewActionsConfig,
      ];
    }
    case ViewIds.MODERNIZED_BLOCK_DIAGRAM: {
      const { groups, viewSettings, viewInstanceId } =
        viewProperties as ModernizedBlockDiagramViewProperties;
      const hasNoGroups = !groups.add.length && !groups.update.length;
      return [
        collapseAll(
          viewSettings,
          viewId,
          viewInstanceId,
          hasNoGroups,
          i18nLocale
        ),
        expandAll(
          viewSettings,
          viewId,
          viewInstanceId,
          hasNoGroups,
          i18nLocale
        ),
        ...defaultViewActionsConfig,
      ];
    }
    case ViewIds.RELATIONSHIPS_3: {
      if (!isRelationshipsSettingsControlsDependencies(viewProperties)) {
        return defaultViewActionsConfig;
      }
      const { viewSettings, groups, rootNode, viewInstanceId } = viewProperties;
      return [
        ...toViewpointModeRelationshipsViewActions({
          dependencies:
            toViewpointModeRelationshipsSettingsControlsDependencies({
              viewSettings,
              groups,
              rootNode,
              viewInstanceId,
            }),
          i18nLocale,
        }),
        ...defaultViewActionsConfig,
      ];
    }
    case ViewIds.DEPENDENCY_MAP_2: {
      if (
        !isViewpointModeDependencyMapSettingsControlsDependencies(
          viewProperties
        )
      ) {
        // error is logged in viewModifiers$
        return defaultViewActionsConfig;
      }
      return toDependencyMapViewpointModeViewActions({
        dependencies: viewProperties,
        i18nLocale,
      });
    }
    case ViewIds.TIMELINE: {
      if (!isTimelineSettingsControlsDependencies(viewProperties)) {
        // error is logged in viewModifiers$
        return defaultViewActionsConfig;
      }
      return [
        ...viewProperties.viewModifiers,
        ...(viewProperties.viewContext.zoomControls ?? []),
      ];
    }
    case ViewIds.COMPONENT_MATRIX_2: {
      const { viewModel } =
        viewProperties as ViewpointModeComponentMatrixViewStreamState;
      const hasNoGroups = [
        ...viewModel.rows.values(),
        ...viewModel.columns.values(),
      ].every(g => g.children.size === 0);
      return [
        {
          id: 'collapseAll',
          label: t(
            i18nLocale,
            'appLayout:viewPaneHeader.collapseAll',
            'Collapse all'
          ),
          iconName: IconName.COLLAPSE_ALL,
          type: SettingsType.BUTTON,
          isDisabled: hasNoGroups,
          onClick: () => dispatchAction(componentMatrixCollapseAll()),
        } satisfies ButtonConfig,
        {
          id: 'expandAll',
          label: t(
            i18nLocale,
            'appLayout:viewPaneHeader.expandAll',
            'Expand all'
          ),
          iconName: IconName.EXPAND_ALL,
          type: SettingsType.BUTTON,
          isDisabled: hasNoGroups,
          onClick: () => dispatchAction(componentMatrixExpandAll()),
        } satisfies ButtonConfig,
        ...defaultViewActionsConfig,
      ];
    }
    case ViewIds.BUBBLE: {
      if (
        !isViewpointModeBubbleChartSettingsControlsDependencies(viewProperties)
      ) {
        // error is logged in viewModifiers$
        return defaultViewActionsConfig;
      }
      return toViewpointModeViewActions({
        dependencies: viewProperties,
        i18nLocale,
      });
    }
    case ViewIds.FLEXIBLE_BLOCK_DIAGRAM: {
      if (!isFlexibleBlockDiagramViewProperties(viewProperties)) {
        // error is logged in viewModifiers$
        return defaultViewActionsConfig;
      }
      const {
        viewSettings,
        isPresentationMode,
        isReadOnly,
        commands,
        hasGroups,
      } = viewProperties;
      const editModeToggle: ToggleConfig = {
        id: 'flexibleBlockDiagramEditMode',
        type: SettingsType.TOGGLE,
        label: t(
          i18nLocale,
          'flexibleBlockDiagram:editMode.label',
          'Edit mode'
        ),
        iconName: IconName.EDIT,
        isActive: !isReadOnly,
        currentValue: !isReadOnly,
        persistent: false,
        onViewSettingsUpdate: () => commands.onReadOnlyChange(!isReadOnly),
      };
      return [
        // Edit affordances are absent in presentation mode.
        ...(isPresentationMode ? [] : [editModeToggle]),
        layoutTypeDropdown(viewSettings, i18nLocale),
        collapseAllButton({
          viewId,
          hasGroups,
          action: flexibleBlockDiagramCollapseAll,
          i18nLocale,
        }),
        expandAllButton({
          viewId,
          hasGroups,
          action: flexibleBlockDiagramExpandAll,
          i18nLocale,
        }),
        ...defaultViewActionsConfig,
      ];
    }
    case ViewIds.NETWORK_3D: {
      if (!isNetwork3DViewProperties(viewProperties)) {
        // error is logged in viewModifiers$
        return defaultViewActionsConfig;
      }
      const { viewSettings, hasGroups } = viewProperties;
      return [
        network3dCollapseAllButton(viewSettings, hasGroups, i18nLocale),
        network3dExpandAllButton(viewSettings, hasGroups, i18nLocale),
        ...defaultViewActionsConfig,
      ];
    }
    default:
      return defaultViewActionsConfig;
  }
};

export { viewActionsOptions };
